import { CommonModule } from '@angular/common'
import { Component, effect, OnDestroy, OnInit, output, ViewChild } from '@angular/core'
import { debounceTime, distinctUntilChanged, Subject, switchMap, takeUntil } from 'rxjs'
import { InternationalizationService } from '../../../../services/internationalization.service'
import {
  ChatAttachmentEntity,
  ChatListItem,
  ChatMessageEntity,
  UserSearchResult,
} from '../../interfaces/chat.interface'
import { MessageChatSessionService } from '../../services/message-chat-session.service'
import { MessageChatSocketService } from '../../services/message-chat-socket.service'
import { MessageChatStateService } from '../../services/message-chat-state.service'
import { MessageChatService } from '../../services/message-chat.service'
import { ChatHeaderComponent } from '../chat-header/chat-header.component'
import { ChatInputComponent } from '../chat-input/chat-input.component'
import { ChatLightboxComponent } from '../chat-lightbox/chat-lightbox.component'
import { ChatMessagesComponent } from '../chat-messages/chat-messages.component'
import { ChatSidebarComponent } from '../chat-sidebar/chat-sidebar.component'
import { CreateGroupModalComponent } from '../modals/create-group/create-group-modal.component'
import { EditGroupModalComponent } from '../modals/edit-group/edit-group-modal.component'
import { GroupInfoModalComponent } from '../modals/group-info/group-info-modal.component'

@Component({
  selector: 'app-message-chat',
  standalone: true,
  imports: [
    CommonModule,
    ChatSidebarComponent,
    ChatHeaderComponent,
    ChatMessagesComponent,
    ChatInputComponent,
    ChatLightboxComponent,
    CreateGroupModalComponent,
    EditGroupModalComponent,
    GroupInfoModalComponent,
  ],
  templateUrl: './message-chat.component.html',
  styleUrl: './message-chat.component.scss',
})
export class MessageChatComponent implements OnInit, OnDestroy {
  readonly closeChat = output<void>()

  @ViewChild(ChatMessagesComponent) messagesComponent!: ChatMessagesComponent
  @ViewChild(ChatInputComponent) inputComponent!: ChatInputComponent
  @ViewChild(ChatLightboxComponent) lightboxComponent!: ChatLightboxComponent
  @ViewChild(CreateGroupModalComponent) createGroupModal!: CreateGroupModalComponent
  @ViewChild(EditGroupModalComponent) editGroupModal!: EditGroupModalComponent
  @ViewChild(GroupInfoModalComponent) groupInfoModal!: GroupInfoModalComponent

  private readonly destroy$ = new Subject<void>()
  private readonly typingSubject = new Subject<{ hasText: boolean }>()
  private readonly searchSubject = new Subject<string>()
  private isCurrentlyTyping = false

  constructor(
    protected chatService: MessageChatService,
    protected socketService: MessageChatSocketService,
    protected state: MessageChatStateService,
    protected translateService: InternationalizationService,
    private sessionService: MessageChatSessionService
  ) {
    effect(() => {
      const user = this.state.pendingOpenUser()
      if (!user) return
      this.state.pendingOpenUser.set(null)
      this.onUserSelected(user)
    })
  }

  ngOnInit(): void {
    this.sessionService.initialize()
    this.loadUsers()
    this.subscribeToSocketEvents()

    this.typingSubject.pipe(takeUntil(this.destroy$)).subscribe(({ hasText }) => {
      const chatId = this.state.selectedChatId()
      if (!chatId) {
        return
      }

      if (!hasText) {
        this.isCurrentlyTyping = false
        this.socketService.emitStopTyping(chatId, this.state.currentUserId())
      } else if (!this.isCurrentlyTyping) {
        this.isCurrentlyTyping = true
        this.socketService.emitTyping(
          chatId,
          this.state.currentUserId(),
          this.state.currentUserName()
        )
      }
    })

    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          this.state.searchingUsers.set(true)
          return this.chatService.findActiveUsers(term.trim())
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (results) => {
          this.state.userSearchResults.set(results)
          this.state.searchingUsers.set(false)
        },
        error: () => this.state.searchingUsers.set(false),
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private loadUsers(): void {
    this.chatService
      .findActiveUsers('')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (users) => this.state.userSearchResults.set(users),
      })
  }

  private loadMessages(chatId: number): void {
    this.state.loadingMessages.set(true)
    this.state.hasMoreMessages.set(true)
    this.chatService
      .findMessages(chatId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (messages) => {
          this.state.messages.set(messages)
          this.state.loadingMessages.set(false)
          this.state.hasMoreMessages.set(messages.length >= 50)
          setTimeout(() => this.messagesComponent?.scrollToBottomAndWaitForMedia())
        },
        error: () => this.state.loadingMessages.set(false),
      })
  }

  onChatSelected(chat: ChatListItem): void {
    this.state.selectedChatId.set(chat.id)
    this.state.resetUnreadCount(chat.id)
    this.state.clearAllTyping()
    this.isCurrentlyTyping = false
    this.loadMessages(chat.id)

    if (chat.lastMessage) {
      this.chatService
        .markAsRead(chat.id, chat.lastMessage.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe()
    }
  }

  onLoadMoreMessages(): void {
    const chatId = this.state.selectedChatId()
    const msgs = this.state.sortedMessages()
    if (!chatId || msgs.length === 0) {
      return
    }

    const oldestId = msgs[0].id
    this.state.loadingMore.set(true)

    this.chatService
      .findMessages(chatId, 50, oldestId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (older) => {
          this.state.prependMessages(older)
          this.state.loadingMore.set(false)
          this.state.hasMoreMessages.set(older.length >= 50)
          this.messagesComponent.restoreScrollAfterPrepend()
        },
        error: () => this.state.loadingMore.set(false),
      })
  }

  onSendMessage(event: { content: string }): void {
    const chatId = this.state.selectedChatId()
    if (!chatId) {
      return
    }

    this.chatService.sendMessage(chatId, event.content).pipe(takeUntil(this.destroy$)).subscribe()
  }

  onSendFile(event: { file: File; content?: string }): void {
    const chatId = this.state.selectedChatId()
    if (!chatId) {
      return
    }

    this.chatService
      .uploadAttachment(chatId, event.file, event.content)
      .pipe(takeUntil(this.destroy$))
      .subscribe()
  }

  onSaveEdit(event: { messageId: number; content: string }): void {
    const chatId = this.state.selectedChatId()
    if (!chatId) {
      return
    }

    this.chatService
      .editMessage(chatId, event.messageId, event.content)
      .pipe(takeUntil(this.destroy$))
      .subscribe()
  }

  onDeleteMessage(msg: ChatMessageEntity): void {
    const chatId = this.state.selectedChatId()
    if (!chatId) {
      return
    }

    this.chatService.deleteMessage(chatId, msg.id).pipe(takeUntil(this.destroy$)).subscribe()
  }

  onOpenLightbox(attachment: ChatAttachmentEntity): void {
    this.lightboxComponent.open(attachment)
  }

  onCreateGroup(event: { name: string; memberIds: number[] }): void {
    const ids = [this.state.currentUserId(), ...event.memberIds]
    this.chatService
      .createChat(event.name, 'GROUP', ids)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (chat) => {
          this.state.chats.update((chats) => [chat, ...chats])
          this.onChatSelected(chat as ChatListItem)
        },
      })
  }

  private creatingChat = false

  onUserSelected(user: UserSearchResult): void {
    if (this.creatingChat) {
      return
    }
    this.creatingChat = true
    const currentUserId = this.state.currentUserId()
    const sortedIds = [currentUserId, user.userId].sort((a, b) => a - b)
    const chatName = `private_${sortedIds[0]}_${sortedIds[1]}`
    this.chatService
      .createChat(chatName, 'PRIVATE', sortedIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (chat) => {
          this.creatingChat = false
          this.state.chats.update((chats) => [chat, ...chats])
          this.state.searchTerm.set('')
          this.onChatSelected(chat as ChatListItem)
        },
        error: () => {
          this.creatingChat = false
          this.chatService
            .findChats()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (chats) => {
                this.state.chats.set(chats)
                this.state.searchTerm.set('')
                const existing = chats.find(
                  (c) =>
                    c.type === 'PRIVATE' && c.participants.some((p) => p.userId === user.userId)
                )
                if (existing) {
                  this.onChatSelected(existing as ChatListItem)
                }
              },
            })
        },
      })
  }

  onShowGroupInfo(): void {
    const chat = this.state.selectedChat()
    if (chat) {
      this.chatService
        .findChatById(chat.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (fullChat) => this.groupInfoModal.open(fullChat),
        })
    }
  }

  onShowEditGroup(): void {
    const chat = this.state.selectedChat()
    if (chat) {
      this.chatService
        .findChatById(chat.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (fullChat) => this.editGroupModal.open(fullChat),
        })
    }
  }

  onSaveGroupEdit(event: {
    chatId: number
    name: string
    addedUserIds: number[]
    removedUserIds: number[]
  }): void {
    this.chatService
      .updateChat(event.chatId, event.name)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedChat) => {
          this.state.chats.update((chats) =>
            chats.map((c) => (c.id === event.chatId ? { ...c, name: updatedChat.name } : c))
          )
        },
      })

    for (const userId of event.addedUserIds) {
      this.chatService
        .addParticipant(event.chatId, userId)
        .pipe(takeUntil(this.destroy$))
        .subscribe()
    }

    for (const userId of event.removedUserIds) {
      this.chatService
        .removeParticipant(event.chatId, userId)
        .pipe(takeUntil(this.destroy$))
        .subscribe()
    }
  }

  onSearchChanged(term: string): void {
    this.searchSubject.next(term)
  }

  onTyping(event: { hasText: boolean }): void {
    this.typingSubject.next(event)
  }

  private subscribeToSocketEvents(): void {
    this.socketService
      .onNewMessage()
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        if (msg.chatId === this.state.selectedChatId()) {
          this.state.addMessage(msg)
          this.state.clearTyping(msg.userId)
          const isOwn = msg.userId === this.state.currentUserId()
          setTimeout(() => this.messagesComponent?.onNewMessage(isOwn))

          this.chatService.markAsRead(msg.chatId, msg.id).pipe(takeUntil(this.destroy$)).subscribe()
        }
      })

    this.socketService
      .onMessageEdited()
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        if (msg.chatId === this.state.selectedChatId()) {
          this.state.updateMessage(msg)
        }
      })

    this.socketService
      .onMessageDeleted()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ chatId, messageId }) => {
        if (chatId === this.state.selectedChatId()) {
          this.state.deleteMessage(messageId)
        }
      })

    this.socketService
      .onUserTyping()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ chatId, userId, userName }) => {
        if (chatId === this.state.selectedChatId()) {
          this.state.setTyping(chatId, userId, userName)
        }
      })

    this.socketService
      .onUserStopTyping()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ chatId, userId }) => {
        if (chatId === this.state.selectedChatId()) {
          this.state.clearTyping(userId)
        }
      })
  }
}
